import * as _ from 'lodash';
import {
  Configuration, FORM_GET_FIELD,
  getAvailableTaskIds,
  getBinaryReversed, Position, Translatable
} from "puzzle";
import React, { ReactElement } from "react";
import { GridCellProps } from "@progress/kendo-react-grid";
import Big from "big.js";
import { LINE_ITEM } from "../Drawer/Tabs/QuotesReview/Types";
import { GRID_COL_INDEX_ATTRIBUTE } from "@progress/kendo-react-grid/dist/npm/constants";
import { Keys } from "@progress/kendo-react-common";
import dayjs from 'dayjs';
import durationPlugin from 'dayjs/plugin/duration'
import { DUE_DATE_INFO } from "../Submission/MultiBatchUpload/Section/Types";
import { GROUPED_TASK, SUBMISSION } from "../MainGrid/Types";
import { isObject } from "lodash";
import { ACTION } from "../SubmissionsList/ActionToolbar/ActionToolbar";
import { TARGETS } from "../Submission/MultiBatchUpload/Types";
import { LANGUAGE_DIRECTIONS } from "../Drawer/Types";
import PoliciesManager from "./Policies";
import { STATUSES } from "../Drawer/Tabs/SupplementaryFiles/Types";
import { MultiSelectOpenEvent } from "@progress/kendo-react-dropdowns/dist/npm/MultiSelect/MultiSelectProps";
import { DETAILS_BATCH_INFO, DETAILS_TARGET_LANGUAGE_INFOS } from "../Drawer/Tabs/Types";
import ReactDOMServer from "react-dom/server";
import GlobalInfoProvider from "./GlobalInfo";
import { tabbable } from "tabbable";

dayjs.extend(durationPlugin);

export type TM_DATA = {
  text: string
  data: TM_CONFIG[]
};

export type TM_CONFIG = {
  default: boolean
  hasGlossary?: boolean
  hasTmConfig?: boolean
  targetLanguageId: string
  targetLanguageDisplayName: string
  languageDirection: string
  notInProject?: boolean
};

/**
 * Pushes copy action to existing 'manage' group
 *
 * @param sortedTasks
 */
const addCopyAction = (sortedTasks: GROUPED_TASK[]): GROUPED_TASK[] => {
  sortedTasks.map((item: GROUPED_TASK) => {
    if (item.groupName === 'manage') {
      item.elements.push({
        "groupWeight": item.elements[0].weight,
        "groupName": "manage",
        "endpoint": null,
        "taskName": "copy URI",
        "selectStyle": 1,
        "taskId": 2,
        "weight": item.elements.length,
        "isDisabled": false
      })
    }
    return item;
  });
  return sortedTasks;
};

/**
 * Matching action pars map.
 * For some actions server sends two tasks and UI should display only one with the same behaviour.
 * In those cases we need to add two sets of actions taskName key<=>value
 */
const matchingTasksPairMethod = () => {
  const map = new Map();
  map.set("manual edit completed submission", 'manual edit submission');
  map.set("manual edit submission", 'manual edit completed submission');
  return map;
}

/**
 * Variable used to export mapped matching pairs
 */
export const hiddenActionPairsMap = matchingTasksPairMethod();

/**
 * List of TSC editors GLT,GLR
 */
export const TSC_EDITORS = [
  'GLR',
  'GLT',
]

/**
 * Array of currently supported editors on NUI
 *
 * SIDE_BY_SIDE is also supported editor but the only way to open is through files tab hyperlink
 */
export const SUPPORTED_EDITORS = [
  ...TSC_EDITORS,
  'EXPORT_VIEWER',
  'GLPLAY_REVIEW',
  'GL_PLAY_TRANSLATION',
  'GL_PLAY_TRANSSCRIPT',
  'TSO',
  'TSR'
];

/**
 * String array of possible names that mark when submission is completed
 */
export const completedSubmissionStatuses: string[] = ['PROCESSED', 'DELIVERED', 'STOPPED', 'CANCELLED']

export const SUBMISSION_STATUSES_MAP_SERVER = {
  ACTIVE: ['FAILED', 'IN_PROCESS', 'READY'],
  CANCELLED: ['CANCELLED', 'STOPPED'],
  COMPLETED: ['PROCESSED'],
  DELIVERED: ['DELIVERED'],
  ON_HOLD: ['WAITING', 'PREFLIGHT']
};

export const SUBMISSION_STATUSES_MAP_UI = {
  FAILED: ['ACTIVE'],
  IN_PROCESS: ['ACTIVE'],
  READY: ['ACTIVE'],
  CANCELLED: ['CANCELLED'],
  PROCESSED: ['COMPLETED'],
  DELIVERED: ['DELIVERED'],
  STOPPED: ['CANCELLED'],
  PREFLIGHT: ['ON_HOLD'],
  WAITING: ['ON_HOLD']
};

export const STATUSES_MAP = {
  SUBMISSION_STATUSES_MAP_SERVER, SUBMISSION_STATUSES_MAP_UI
};

/**
 *  Copy string to clipboard
 */
export const copyToClipboard = (text: string): void => {
  let el = document.createElement('textarea');
  el.value = text;
  document.body.appendChild(el);
  el.select();
  document.execCommand('copy');
  document.body.removeChild(el);
};

export const quickFilterChangeDelay = 1000;

export type PALETTE = {
  [key: string]: string
};

export const COLOR_PICKER_PALETTE_LIGHT: PALETTE = {
  'no_color': 'rgba(255, 255, 255, 0)',
  'yellow': 'rgba(255, 215, 0, 1)',
  'orange': 'rgba(255, 125, 51, 1)',
  'red': 'rgba(211, 0, 4, 1)',
  'magenta': 'rgba(241, 85, 127, 1)',
  'purple': 'rgba(124, 89, 219, 1)',
  'blue': 'rgba(0, 115, 234, 1)',
  'light_blue': 'rgba(101, 172, 255, 1)',
  'dark_green': 'rgba(0, 138, 18, 1)',
  'light_green': 'rgba(72, 207, 89, 1)',
  'light_grey': 'rgba(207, 214, 226, 1)',
};

export const COLOR_PICKER_PALETTE_DARK: PALETTE = {
  'no_color': 'rgba(255, 255, 255, 0)',
  'yellow': 'rgba(255, 210, 96, 1)',
  'orange': 'rgba(255, 156, 101, 1)',
  'red': 'rgba(238, 3, 3, 1)',
  'magenta': 'rgba(241, 85, 127, 1)',
  'purple': 'rgba(161, 133, 234, 1)',
  'blue': 'rgba(29, 140, 255, 1)',
  'light_blue': 'rgba(160, 204, 255, 1)',
  'dark_green': 'rgba(32, 174, 51, 1)',
  'light_green': 'rgba(120, 229, 133, 1)',
  'light_grey': 'rgba(103, 113, 136, 1)'
};

/**
 * Get different color palette for each theme
 */
export const getColorPalette = () => {
  let lightTheme = GlobalInfoProvider.getThemeType();
  let palette = lightTheme ? COLOR_PICKER_PALETTE_LIGHT : COLOR_PICKER_PALETTE_DARK;

  return palette;
}

/**
 * Adds deleted targetLanguages to languageDirections
 * @param languageDirections
 * @param targetLangs
 * @param batchInfos
 */
export const addDeletedTargetLanguages = (languageDirections: LANGUAGE_DIRECTIONS[], targetLangs: string[], batchInfos: DETAILS_BATCH_INFO[]): LANGUAGE_DIRECTIONS[] => {
  let deletedLanguages: LANGUAGE_DIRECTIONS[] = [];
  targetLangs.forEach((lang: string) => {
    let matchedItem = _.find(languageDirections, { targetLanguage: lang });
    if (!matchedItem) {
      deletedLanguages.push({
        default: false,
        hasGlossary: false,
        hasTmConfig: false,
        notInProject: true,
        targetLanguage: lang
      })
    }
  });

  let directionsWithDeletedLangs = [...languageDirections, ...deletedLanguages];
  //sorts directionsWithDeletedLangs based on order in targetLangs
  directionsWithDeletedLangs.sort((a, b) => targetLangs.indexOf(a['targetLanguage']) > targetLangs.indexOf(b['targetLanguage']) ? 1 : -1);
  directionsWithDeletedLangs = localizeTargetLanguages(directionsWithDeletedLangs, batchInfos);

  return directionsWithDeletedLangs;
}

/**
 * Maps statuses to match with server side statuses
 *
 * @param statuses
 * @param statusType
 */
export const getMappedSubmissionStatuses = (statuses: string[], statusType: string) => {
  let statusArray: any = [];
  let item = statuses.length;
  while (item--) {
    let submissionStatusesMapElement = (STATUSES_MAP as any)[statusType][statuses[item]];
    if (submissionStatusesMapElement) {
      statusArray = [...statusArray, ...submissionStatusesMapElement]
    } else {
      statusArray.push(statuses[item])
    }
  }
  return _.uniq(statusArray);
};

/**
 * Is checking to see if there are any targets(Files) that do not have status of PROCESSED', 'DELIVERED', 'STOPPED' or 'CANCELLED'
 *
 * @param targets
 */
export const hasUnfinishedTargets = (targets: TARGETS[]) => {
  let hasUnfinishedTargets: boolean = targets ? targets.some((target: { status: string; }) => {
    return !completedSubmissionStatuses.includes(target.status);
  }) : true;
  return hasUnfinishedTargets;
}

export const disableActions = (records: any[]): string[] => {
  let binaryTasksAndProcessing;
  let availableTaskIds: number[];
  let availableActions: string[] = [];
  let newActions;
  records.forEach((record: any) => {
    if (record.availableTasks) {
      binaryTasksAndProcessing = getBinaryReversed(record.availableTasks);
      availableTaskIds = getAvailableTaskIds(binaryTasksAndProcessing);

      newActions = Array.isArray(record.tasks) ? record.tasks.filter((task: any) => {
        const isTaskAvailable: boolean = availableTaskIds.includes(task.taskId);
        return isTaskAvailable;
      }).map((item: any) => item.taskName) : [];

      availableActions = !_.isEmpty(availableActions) ? _.intersection(availableActions, newActions) : newActions;
    }
  });

  return availableActions;
};

/**
 * Focuses next or previous element depending on tab or shift+tab
 *
 * @param containerEl
 * @param event
 */
export const focusNextElement = (containerEl: HTMLElement, event: KeyboardEvent) => {
  if (containerEl && event.key === 'Tab') {
    event.preventDefault();
    const direction = event.shiftKey ? 'previous' : 'next';
    const focusableElements = tabbable(containerEl);
    const activeElement = document.activeElement as HTMLElement;
    const currentIndex = focusableElements.indexOf(activeElement);
    if (currentIndex !== -1) {
      let nextIndex;
      if (direction === 'next') {
        nextIndex = (currentIndex + 1) % focusableElements.length;
      } else {
        nextIndex = (currentIndex - 1 + focusableElements.length) % focusableElements.length;
      }

      focusableElements[nextIndex]?.focus();
    }
  }
}

/**
 * Set html document title
 * @param title
 */
export const setDocumentTitle = (title: string): void => {
  const instanceName = GlobalInfoProvider.getInstanceName();
  document.title = `${instanceName} - ${title}`;
};

/**
 * Creates proper css class taking into account all given flags
 * @param data
 */
export const createTargetLanguageTagClass = (data: LANGUAGE_DIRECTIONS | TM_CONFIG): string => {
  const {
    notInProject,
    hasTmConfig,
    hasGlossary
  } = data;

  let className: string = ''; // not class means that it will be blue tag
  if (notInProject) {
    className = 'tp-red-tag';
  } else if (hasTmConfig) {
    className = hasGlossary ? 'tp-purple-tag' : 'tp-green-tag';
  }

  return className;
};

export const tmTagRenderer = (data: TM_DATA, li: ReactElement<any>, dispatch: any, projectId?: number, paClientId?: number) => {
  let className = `${li.props.className} ${createTargetLanguageTagClass(data.data[0])}`;
  let onClick: (e: any) => void = (e: any) => e.syntheticEvent?.stopPropagation();
  if (!data.data[0].notInProject && data.data[0].hasTmConfig) {
    onClick = (e: any) => {
      dispatch('openTMConfiguration', '', {
        hasGlossary: data.data[0].hasGlossary,
        paClientId,
        projectId,
        tmConfigurationData: data.data[0]
      });
    };
  }
  return React.cloneElement(
    li,
    { ...li.props, className, removable: !li.props.disabled },
    [<span key={li.props.id} onClick={onClick}>{data.text}</span>, li.props.children]
  );
};


/**
 * Method for checking if grid has virtual scroll
 * @param domNode
 * @param headerElement
 * @param currentElement
 */
export const checkVerticalScroll = (domNode: any, headerElement: any, currentElement?: HTMLElement) => {
  let className = 'tp-scroll';
  if (domNode && headerElement) {
    const hasScroll = currentElement ? (domNode.scrollHeight + headerElement.clientHeight) > currentElement.clientHeight : domNode.scrollHeight > domNode.clientHeight;
    if (headerElement.classList.contains(className)) {
      if (!hasScroll) {
        headerElement.classList.remove(className)
      }
    } else {
      if (hasScroll) {
        headerElement.classList.add(className)
      }
    }
  }
}

/**
 *
 * If cell does not have defined render in CELL_RENDER_MAP, defaultCellRender is used.
 *
 * @param translate
 * @param loadingCellRender
 * @param cellProps
 */
export const defaultCellRender = (translate: Function, loadingCellRender: Function, cellProps: GridCellProps) => {
  const { dataItem, field } = cellProps;
  let valueItem;
  if (field) {
    valueItem = _.get(dataItem, field);
  }

  let loadingCell = loadingCellRender(cellProps);
  if (!loadingCell) {
    loadingCell = <> {isObject(valueItem) ? (valueItem as { name: string }).name : valueItem} </>;
  }
  return loadingCell;
};

/**
 *  Handle arrow (Up/Down) navigation
 * @param me
 * @param elementNodeList
 * @param e
 */
export function setArrowNavigation(me: any, elementNodeList: any[], e: any) {
  e.preventDefault();
  let keyCode = e.keyCode;

  switch (keyCode) {
    case Keys.down:
      if (me.columnPopupArrowIndex >= elementNodeList.length - 1) {
        me.columnPopupArrowIndex = -1
      }
      elementNodeList[++me.columnPopupArrowIndex].focus();
      break;
    case Keys.up:
      if (me.columnPopupArrowIndex === 0) {
        me.columnPopupArrowIndex = elementNodeList.length;
      }
      elementNodeList[--me.columnPopupArrowIndex].focus();
      break;
    case Keys.esc:
      me.setState({ showGridColumnSettings: false }, () => {
        me.settingsButtonRef?.firstChild?.firstChild?.focus();
      });
      break;
    case Keys.space:
    case Keys.enter:
      const arrayOfColumns = me.mapGridConfigItems();
      const currentItemClicked = arrayOfColumns[me.columnPopupArrowIndex];
      me.onGridColumnToggle(currentItemClicked, me.columnPopupArrowIndex);
      break;
    default:
      break;
  }
}

/**
 *
 * Calculate line items total per one quote
 *
 * @param lineItems
 * @param withPercentage
 * @param withOptional
 * @param optionalOnlyServerApprove
 */
export const calculateLineItemsTotal = (lineItems: LINE_ITEM[], withPercentage: boolean, withOptional: boolean, optionalOnlyServerApprove: boolean): Big => {
  let total = Big(0);
  let lineItemsLength = lineItems.length;
  let current;
  let currentOptionalTotal;
  let percentageLineItems: LINE_ITEM[] = [];
  let isOptionalApproved = false;
  while (lineItemsLength) {
    lineItemsLength--;
    current = lineItems[lineItemsLength];
    if (current.isOptional) {
      isOptionalApproved = optionalOnlyServerApprove ? !!current.accepted : (current.approved || current.accepted);
      if (withOptional && isOptionalApproved) {
        if (current.unit === 'PERCENTAGE') {
          percentageLineItems.push(current);
        } else {
          currentOptionalTotal = Big(current.quantity).times(current.rate);
          total = total.plus(currentOptionalTotal.toNumber());
        }
      }
    } else {
      if (current.unit === 'PERCENTAGE') {
        percentageLineItems.push(current);
      } else {
        total = total.plus(current.total || 0);
      }
    }
  }

  if (withPercentage) {
    let totalWithoutPercentage = total;
    percentageLineItems.forEach((item: LINE_ITEM) => {
      if (item.rate) {
        let percentageValue = Big(item.rate).div(100).times(totalWithoutPercentage);
        total = total.plus(percentageValue);
      }
    });
  }
  return total;
};

/**
 * Empty quick filter cell (accessibility purposes)
 */
export const defaultFilterCell = () => {
  return <div>&nbsp;</div>;
};

/**
 * Array of statuses that allow quote report download
 */
export const QUOTE_DOWNLOAD_ENABLED: string[] = ['APPROVED', 'PROVIDED', 'UPDATED'];

/**
 * Function that is designed to deal with the issue of dynamic adding of tabindex.
 * Problem: If you add tabindex before the next element that should have been focused, it won't work because tab index focusing works by keeping a reference to the next element that should be focused.
 * Solution is to focus the body or any other element and then return focus to the element that had the focus so that tab index updates correctly.
 * Timeout is necessary due to callstack (it is required so that tabindex focus gets updated after rendering of the updated element)
 * @param ref of the component where the current focus is located
 */
export const resetFocus = (ref: any) => {
  document.body.tabIndex = -1;
  document.body.focus()

  // Got to put timeout with 0 miliseconds, so that it goes at the end of callstack. By doing so .focus will be called after rendering and changing of components.
  setTimeout(() => {
    ref.current.comboRef.component.focus();
  }, 0)
}

/**
 * Create <td> attributes
 * @param cellProps
 */
export const getTableTdAccessibilityProps = (cellProps: GridCellProps) => {
  const tdProps = {
    colSpan: cellProps?.colSpan,
    role: 'gridcell',
    'aria-colindex': cellProps?.ariaColumnIndex,
    'aria-selected': cellProps?.isSelected,
    ...{ [GRID_COL_INDEX_ATTRIBUTE]: cellProps?.columnIndex }
  };

  return tdProps; //note if grid has navigatable={true} navigation props should be copied too useTableKeyboardNavigation(cellProps.id);
};

/**
 * Set's table caption
 * @param scope
 * @param text
 */
export const setTableCaption = (scope: HTMLElement | null, text: string) => {
  let domNode = scope;
  if (domNode) {
    let tables: HTMLTableElement[] = [...domNode.getElementsByTagName('table')];
    if (tables.length) {
      let caption = tables[0].createCaption();
      caption.textContent = text;
      caption.className = 'sr-only';
    }
  }
};

/**
 * set accessibility for menu
 */
export const setAccessibilityModeForMenu = (menuWrapper: HTMLDivElement, label: string): void => {
  const li = menuWrapper?.querySelector('li, .k-item');
  li?.setAttribute('aria-label', label);
}
/**
 * Return matching submission phases' status
 */
export const getSubmissionPhaseStatus = (phase: any): string => phase.batchTask ? getAutoPhaseStatus(phase) : getHumanPhaseStatus(phase.phaseStatus);

/**
 * Return matching status for automatic phase
 */
export const getAutoPhaseStatus = ({ phaseStatus, phaseName }: { phaseStatus: string; phaseName: string }): string => {
  switch (phaseStatus) {
    case 'FAILED':
    case 'COMPLETED':
    case 'SKIPPED':
      return phaseStatus;
    case 'PROCESSED':
    case 'DELIVERED':
      return 'COMPLETED';
    case 'WAITING':
      return 'NOT_CLAIMED';
    case 'CANCELLED':
      return 'CANCELLED';
    case 'READY':
      if (phaseName === 'On Hold') {
        return 'ON_HOLD';
      } else {
        return 'IN_PROGRESS';
      }
    default:
      return 'IN_PROGRESS';
  }
};

/**
 * Return matching status for human phase
 */
export const getHumanPhaseStatus = (phaseStatus: string): string => {
  let status = 'NOT_CLAIMED';
  switch (phaseStatus) {
    case 'PROCESSED':
    case 'DELIVERED':
      status = 'COMPLETED';
      break;
    case 'IN_PROCESS':
    case 'READY':
      status = 'IN_PROGRESS';
      break;
    case 'CANCELLED':
    case 'SKIPPED':
      status = phaseStatus;
      break;
    default:
      break;
  }
  return status;
};

export const ANNOUNCEMENTS_TYPE = {
  UPLOAD: 'upload',
  DELETE: 'delete'
};

/**
 * Method for formatting placeholder for TPDatePicker
 * @param translate
 */
export const getFormattedDate = (translate: Function) => {
  return {
    day: translate('date.day'),
    month: translate('date.month'),
    year: translate('date.year'),
    hour: translate('date.hour'),
    minute: translate('date.minute'),
  }
};

type supportedAttributes = 'aria-colcount' | 'aria-rowcount' | 'role';

/**
 * Removes unnecessary aria attributes from grid (a11y)
 * @param scope
 */
export const removeUnnecessaryAttributes = (scope: Element | null) => {
  let domNode = scope;
  if (domNode) {
    if (!(domNode.getAttribute('role') === "grid")) {
      const array: Element[] = [...domNode.querySelectorAll('[role="grid"]')];
      if (array.length) {
        domNode = array[0] as Element;
      }
    }
    let attributes: supportedAttributes[] = ['aria-colcount', 'aria-rowcount'];
    if (domNode.getAttribute('role') === "treegrid") {
      attributes.push('role');
    }
    attributes.forEach((attribute: string) => {
      domNode?.hasAttribute(attribute) && domNode.removeAttribute(attribute);
    });
  }
};

/**
 * Check if passed dueDate is expired
 * @param dueDate
 */
export const isDateExpired = (dueDate: number): boolean => {
  let isExpired: boolean = false;
  if (dueDate) {
    const dueDateDayjs = dayjs(new Date(dueDate));
    const dateDifference = dayjs.duration(dueDateDayjs.diff(new Date())).asHours();
    isExpired = dateDifference < 0;
  }
  return isExpired;
};

export const isSubmissionDueSoon = (dueDate: any, dueSoon: any): boolean => {
  let isDueSoon: boolean = false;
  if (dueDate && dueSoon) {
    const dueDateDayjs = dayjs(new Date(dueDate));
    const dateDifference = dayjs.duration(dueDateDayjs.diff(new Date())).asHours();
    isDueSoon = dateDifference <= dueSoon && dateDifference >= 0;
  }
  return isDueSoon;
};

export const SUBMISSION_STATUSES_MAP_SERVER_CORE_UI = {
  ACTIVE: ['FAILED', 'IN_PROCESS', 'READY'],
  CANCELLED: ['CANCELLED'],
  COMPLETED: ['PROCESSED', 'DELIVERED', 'STOPPED'],
  ON_HOLD: ['WAITING', 'PREFLIGHT']
};

/**
 * Returns matching values for search data as a JSX. (from key to display value)
 */
export const getMatchingDisplayName = (values: (string | LANGUAGE_DIRECTIONS)[], key: string, initializeUserData: any, isTargetLanguage = false, isBatchContent: boolean = false) => {
  const tagClassName = isBatchContent ? '' : 'k-chip k-chip-md k-rounded-md k-chip-solid k-chip-solid-base';
  const returnValues = getPureMatchingDisplayName(values, key, initializeUserData, isTargetLanguage);
  return (
    <>
      {isTargetLanguage ?
        (returnValues as LANGUAGE_DIRECTIONS[]).map((item: LANGUAGE_DIRECTIONS, index: number) =>
          (
            <div
              className={`${tagClassName} ${createTargetLanguageTagClass(item)}`}
              key={`tp-target-lang-tag-${index}`}
            >
              {item.targetLanguage}
            </div>
          )) :
        returnValues.join(', ')
      }
    </>
  );
};

/**
 * Returns matching values for search data. (from key to display value)
 *
 * @param values
 * @param key
 * @param initializeUserData
 * @param isTargetLanguage
 */
export const getPureMatchingDisplayName = (values: (string | LANGUAGE_DIRECTIONS)[], key: string, initializeUserData: any, isTargetLanguage = false): (string | LANGUAGE_DIRECTIONS)[] => {
  const { searchBar } = initializeUserData;
  for (let item in searchBar) {
    if (searchBar[item].name === key) {
      values = values.map((value: string | LANGUAGE_DIRECTIONS) => {
        let returnValue = isTargetLanguage ? ((value as LANGUAGE_DIRECTIONS).targetLanguage) : value as string;
        let current;
        let searchBarValues = searchBar[item].values;
        for (let item1 = 0; item1 < searchBarValues.length; item1++) {
          current = searchBarValues[item1];
          if (current[1] === returnValue) {
            returnValue = current[0];
            break;
          }
        }
        return isTargetLanguage ? { ...value as LANGUAGE_DIRECTIONS, targetLanguage: returnValue } : returnValue;
      })
    }
  }
  return values;
};

/**
 * Returns path to the locales
 *
 * @param columnName
 */
export const getStatusesLocalizationPath = (columnName: string): string => {
  let path: string;
  let statuses = 'statuses';
  switch (columnName) {
    case 'quoteStatus':
    case 'quoteStatuses':
      path = `QUOTES.${statuses}`;
      break;
    case 'taskStatus':
    case 'taskStatusValues':
      path = `TASK.${statuses}`;
      break;
    case 'status':
    case 'submissionStatusValues':
      path = `SUBMISSION.${statuses}`;
      break;
    case 'budgetStatus':
    case 'vendorBudgetStatuses':
      path = `BUDGET.${statuses}`;
      break;
    default:
      path = 'PRIORITY';
      break;
  }
  return path;
};

/**
 * Returns unix date regardless is it passed value date or unix date already. Exception is undefined passed value, in that case returns null
 *
 * @param date
 */
export const getUnixDate = function (date?: any) {
  let unixDate = null;
  if (_.isDate(date)) {
    unixDate = date.getTime();
  } else if (_.isNumber(date)) {
    unixDate = date;
  }
  return unixDate;
};

/**
 * Custom coloring for targetLanguage tagField(dropdown items)
 * @param li
 * @param itemProps
 */
export const targetLanguageItemColoring = (li: any, itemProps: any) => {
  let className = li.props.className;
  if (itemProps.dataItem.notInProject) {
    className = 'hidden';
  } else if (itemProps.dataItem.hasTmConfig) {
    if (itemProps.dataItem.hasGlossary) {
      className = className.concat(' tp-purple-list-item');
    } else {
      className = className.concat(' tp-green-list-item');
    }
  } else if (itemProps.dataItem.default) {
    className = className.concat(' tp-green-list-item');
  }

  return {
    ...li.props,
    className
  };
};

/**
 * Finds localization for every language direction
 * @param directionsWithDeletedLangs
 * @param batchInfos
 */
export const localizeTargetLanguages = (directionsWithDeletedLangs: LANGUAGE_DIRECTIONS[], batchInfos: DETAILS_BATCH_INFO[]) => {
  let targetLanguages: {
    targetLanguage: string,
    targetLanguageLocale: string
  }[] = [];

  batchInfos.forEach((batch: DETAILS_BATCH_INFO) => {
    batch.targetLanguageInfos?.forEach((lang: DETAILS_TARGET_LANGUAGE_INFOS) => {
      if (!_.find(targetLanguages, { targetLanguage: lang.targetLanguage })) {
        targetLanguages.push({
          targetLanguage: lang.targetLanguage,
          targetLanguageLocale: lang.targetLanguageDisplayName
        })
      }
    })
  });

  directionsWithDeletedLangs = directionsWithDeletedLangs.map((item) => ({
    ...item,
    targetLanguageLocale: _.find(targetLanguages, { targetLanguage: item.targetLanguage })?.targetLanguageLocale || item.targetLanguage
  }));

  return directionsWithDeletedLangs;
};

/**
 * Adjust existing phase due dates to match new due date
 */
export const adjustPhaseDueDates = function (dueDatesInfo: DUE_DATE_INFO[], dueDate: number) {
  let i = 0;
  let dueDatesInfoLength = dueDatesInfo.length;
  let current;
  while (i < dueDatesInfoLength) {
    current = dueDatesInfo[i];
    if (current.dueDate > dueDate) {
      current.dueDate = dueDate;
    }
    i++;
  }

  return dueDatesInfo;
}

/**
 *  Sorts tasks items relative to group weights and item weights
 *  @param actions
 */
export const taskSorter = (actions: ACTION[]): GROUPED_TASK[] => {
  let sortedTasks = actions.reduce((array: GROUPED_TASK[], item: ACTION) => {
    let cur = _.find(array, (i) => i.groupName === item.groupName);
    if (cur) {
      let position = _.sortedIndex(cur.elements.map((i: any) => i.weight), item.weight);
      cur.elements.splice(position, 0, {
        ...item,
        isDisabled: false
      });
    } else {
      let position = _.sortedIndex(array.map((i) => i.position), item.groupWeight);
      array.splice(position, 0, {
        groupName: item.groupName,
        position: item.groupWeight,
        elements: [{
          ...item,
          isDisabled: false
        }]
      });
    }
    return array;
  }, []);
  addCopyAction(sortedTasks);
  return sortedTasks;
}

/**
 * Check if action is available for selected submission
 *
 * @param submission
 * @param actionName
 */
export const isActionAvailable = (submission: SUBMISSION, actionName: string): boolean => {
  const decodedTasks = disableActions([submission]);
  return decodedTasks.includes(actionName);
}

/**
 * Remove mass actions from actions array (hide them from UI) and on single actions add massTaskName
 * @param {ACTION[]} actions
 */
export const prepareMassActions = (actions: ACTION[]) => {
  let massActions = _.filter(actions, (item: ACTION) => _.startsWith(item.taskName, 'mass '));
  let i = 0;

  while (i < massActions.length) {
    let current = massActions[i];
    let actionIndex = _.findIndex(actions, { taskName: current.taskName.split('mass ').pop() });
    if (actionIndex !== -1) {
      actions[actionIndex].massTaskName = current.taskName;
      let currentIndex = _.findIndex(actions, { taskName: current.taskName });
      actions.splice(currentIndex, 1);
    } else {
      //non-standard case when single action taskName is not 'mass actionName' (e.g reserve for mass claim)
      actionIndex = _.findIndex(actions, { taskName: current.taskName });
      if (actionIndex !== -1) {
        actions[actionIndex] = {
          ...current,
          massTaskName: current.taskName,
          taskId: 0
        }
      }
    }
    i++;
  }

  return actions;
}

/**
 * Require and set appropriate dayjs locale
 * @param {string} cookieLocale
 */
export const setupDayjsLocale = (cookieLocale: string) => {
  if (cookieLocale) {
    const availableLocales = require('dayjs/locale.json')
    let localeIndex = _.findIndex(availableLocales, { key: cookieLocale });

    if (localeIndex === -1) {
      cookieLocale = cookieLocale.split('-')[0];
      let localeIndex = _.findIndex(availableLocales, { key: cookieLocale });
      if (localeIndex === -1) {
        cookieLocale = 'en'; //fallback to default english locale
      }
    }
    require(`dayjs/locale/${cookieLocale}.js`);
    dayjs.locale(cookieLocale);
  }
}

/**
 * Returns editorSessionId/payloadId url param
 * @param editorParam
 * @param item
 */
export const getEditorId = (editorParam: string, item: string): boolean => {
  let editorItem = item.split("=");
  return editorItem[0] === editorParam;
};

/**
 * Returns url string with submissionId param
 *
 * @param submissionId
 */
export const getUrlWithParams = (submissionId: string): string => {
  const { BASE_ROUTE_NAME } = Configuration.get();
  let urlToCopy = new URL(document.location.origin + BASE_ROUTE_NAME);
  urlToCopy.searchParams.append('submissionId', submissionId);
  return urlToCopy.toString();
};

/**
 * Gets online editor identifier from url (editorSessionId, payloadId)
 * @param url
 * @param editorParam
 */
export const getOnlineEditorIdentifier = (url: string, editorParam: string = 'editorSessionId'): string => {
  let item = url.split("&").find((param) => getEditorId(editorParam, param)) as string;
  let editorId = item.split("=")[1];
  return editorId
};

/**
 * Translate custom attributes name
 * @param {string} name - Custom attribute name
 */
export const translateCustomAttributes = (name: string) => {
  let translatedText;
  let translatePath = `CustomAttributes.${name}`;
  translatedText = Translatable.t(translatePath);
  if (translatePath === translatedText) {
    translatedText = name;
  }

  return translatedText;
}

/**
 * Checks if supplementary files tab should be shown
 * @param statuses
 * @param collateralDownloadEnabled
 */
export const showSupplementaryFiles = (statuses: STATUSES, collateralDownloadEnabled?: boolean) => {
  const { taskStatus, submissionStatus } = statuses
  let showSupplementaryFiles: boolean = false;
  let policiesAreActive = taskStatus ? PoliciesManager.hasAddDelierableCollateralPolicy && collateralDownloadEnabled : PoliciesManager.hasAddDelierableCollateralPolicy
  let supplementaryFilesNotAllowed = taskStatus === 'AVAILABLE' || ['WAITING', 'PREFLIGHT'].includes(submissionStatus)
  if (policiesAreActive && !supplementaryFilesNotAllowed) {
    showSupplementaryFiles = true
  }
  return showSupplementaryFiles
}

type popupAlignmentReturnObj = {
  popupClass: string | undefined,
  anchorAlign: {
    horizontal: 'right',
    vertical: 'bottom'
  },
  popupAlign: {
    horizontal: 'right',
    vertical: 'top'
  }
};

/**
 * Alignment of dropdown buttons and popupClass
 * @param popupClass
 */
export const popupAlignment = (popupClass?: string): popupAlignmentReturnObj => ({
  popupClass: popupClass,
  anchorAlign: {
    horizontal: 'right',
    vertical: 'bottom'
  },
  popupAlign: {
    horizontal: 'right',
    vertical: 'top'
  }
})


/**
 * Hides dropdown on open if field is disabled
 *
 * @param event
 */
export const preventDisabledTagToOpen = (event: MultiSelectOpenEvent) => {
  if (event.target.props.disabled) {
    event.target.setState({
      opened: false
    });
  }
};

/**
 * Getter for translation for commonly used statuses (Submission,Quote,File,Task,TaskFile ...)
 * example translation:Util.SUBMISSION.statuses.ACTIVE
 * @param path
 */
export const commonLocalizationGetter = (path: string): string => {
  const translation = Translatable.t(`translation:Util.${path}`);
  return translation;
}

/**
 * Convert ReactElement to static markup string if some custom tags is used in localization
 */
export const checkAndConvertToStaticMarkup = (locale: string | ReactElement) => {
  let result = locale;
  if (_.isObject(locale)) {
    result = ReactDOMServer.renderToStaticMarkup(locale);
  }
  return result;
}

/**
 * Parses remote data to proper format for remote combo quick filters
 * @param data
 * @param field
 */
export const parseRemoteTagFieldData = (data: {
  supportedValues: { name: string, value: string }[]
}, field: string = '') => {
  const { supportedValues = [] } = data;
  let localizeFirstItem = field === 'assignedTo';
  const parsedData = supportedValues.map(({ name, value }, index: number) => ({
    name: index === 0 && localizeFirstItem ? commonLocalizationGetter(`UNCATEGORIZED.${name}`) : name,
    value
  }));

  return parsedData;
};

/**
 * While request's is in progress(loadInProgressFlag), removes all props except value prop from form validation data PDII-29449
 * @param formFields
 * @param loadInProgressFlag
 */
export const deleteFormControlParams = (formFields: FORM_GET_FIELD[], loadInProgressFlag: boolean) => {
  if (loadInProgressFlag) {
    formFields.forEach((item: FORM_GET_FIELD) => {
      Object.keys(item).forEach((key: string) => 'value' !== key && delete (item as any)[key])
    })
  }
};

export const getFavoritesFilterCount = (classInstance: any) => {
  let filterIds = classInstance.favoritesFilterCountIds;
  if (filterIds.length) {
    classInstance.dispatch('pingForFilterSubmissionCount', {
      dispatcherId: 'filterCountDispatcher',
      pos: Position.end
    }, {
      countParams: {
        filterIds: [filterIds[0]],
      },
      countUrl: classInstance.favoriteFilterUrl,
      filterModuleName: classInstance.moduleName
    });
  } else {
    classInstance.dispatch('stopPingingRequestForFilterCount', Position.end);
  }
}

export const pingForFavoritesFilterCount = (responseData: any, classInstance: any) => {
  if (classInstance.isComponentMounted) {
    let { filters } = classInstance.state;
    let countProperty = classInstance.favoritesFilterCountProperty;
    let filtersCount = responseData.model.filtersCount[0];
    let filterId = filtersCount.filterId
    let filterCount = filtersCount[countProperty];
    classInstance.favoritesFilterCountIds.splice(0, 1);
    getFavoritesFilterCount(classInstance);
    let filterIndex = _.findIndex(filters, { filterId });
    let filterItem = filters[filterIndex];
    filters[filterIndex] = { ...filterItem, [countProperty]: filterCount };
    classInstance.setState({ filters });
  }
};
